import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useNavigate, useParams } from 'react-router-dom';

function buildRows(batchId) {
  const rows = Array.from({ length: 64 }, (_, idx) => {
    const sampleNumber = String(idx + 1).padStart(3, '0');
    const sampleId = `SMP-${batchId || 'BATCH-0000'}-${sampleNumber}`;
    const value = (((idx * 37) % 180) - 90) / 100;
    return {
      id: sampleId,
      value: Number(value.toFixed(2)),
      reportedResult: value >= 0 ? 'Positive' : 'Negative',
      releaseStatus: 'Under Review',
    };
  });

  // A few intentionally out-of-range values.
  rows[7].value = 1.42;
  rows[7].reportedResult = 'Positive';
  rows[21].value = -1.28;
  rows[21].reportedResult = 'Negative';
  rows[40].value = 1.91;
  rows[40].reportedResult = 'Positive';
  rows[55].value = -1.67;
  rows[55].reportedResult = 'Negative';
  return rows;
}

export default function LabResultBatchDetail({
  backPath = '/mock/lab-dashboard#result-batchs',
  backLabel = 'Back to Result Batchs',
} = {}) {
  const navigate = useNavigate();
  const { batchId } = useParams();
  const initialRows = useMemo(() => buildRows(batchId), [batchId]);
  const [rows, setRows] = useState(initialRows);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [releaseStatusFilter, setReleaseStatusFilter] = useState('all');
  const [rejectingRowId, setRejectingRowId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const filteredRows = rows.filter((row) => {
    if (releaseStatusFilter === 'all') return true;
    return row.releaseStatus === releaseStatusFilter;
  });
  const normalRangeIds = filteredRows
    .filter(
      (row) =>
        row.value >= -1.0 &&
        row.value <= 1.0 &&
        row.releaseStatus !== 'Rejected'
    )
    .map((row) => row.id);
  const selectedNormalCount = normalRangeIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = normalRangeIds.length > 0 && selectedNormalCount === normalRangeIds.length;
  const partiallySelected = selectedNormalCount > 0 && selectedNormalCount < normalRangeIds.length;

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        normalRangeIds.forEach((id) => next.add(id));
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      normalRangeIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const markReviewed = () => {
    if (selectedIds.size === 0) return;
    setRows((prev) =>
      prev.map((row) =>
        selectedIds.has(row.id) ? { ...row, releaseStatus: 'Final Review' } : row
      )
    );
  };

  const rejectReasons = [
    'Outlier value requires re-run',
    'Insufficient sample volume',
    'Label mismatch',
    'QC flag on instrument',
    'Specimen integrity concern',
  ];

  const confirmRejectRow = () => {
    if (!rejectingRowId || !rejectReason) return;
    setRows((prev) =>
      prev.map((row) =>
        row.id === rejectingRowId ? { ...row, releaseStatus: 'Rejected' } : row
      )
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(rejectingRowId);
      return next;
    });
    setRejectingRowId(null);
    setRejectReason('');
  };

  return (
    <Container disableGutters sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack spacing={2}>
          <Box>
            <Button variant="text" onClick={() => navigate(backPath)}>
              {backLabel}
            </Button>
          </Box>

          <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h4" component="h1">
                    Batch: {batchId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    64 samples in this batch. Values outside -1.0 to 1.0 are highlighted.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <TextField
                    select
                    label="Release Status"
                    value={releaseStatusFilter}
                    onChange={(event) => setReleaseStatusFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="Under Review">Under Review</MenuItem>
                    <MenuItem value="Final Review">Final Review</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </TextField>
                  <Button variant="contained" onClick={markReviewed} disabled={selectedIds.size === 0}>
                    Mark Reviewed
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={partiallySelected}
                        onChange={(event) => handleSelectAll(event.target.checked)}
                        inputProps={{ 'aria-label': 'Select all rows' }}
                      />
                    </TableCell>
                    <TableCell>Sample ID</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Reported Result</TableCell>
                    <TableCell>Release Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row) => {
                    const outOfRange = row.value < -1.0 || row.value > 1.0;
                    const isRejected = row.releaseStatus === 'Rejected';
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => toggleRow(row.id)}
                        sx={{
                          cursor: 'pointer',
                          ...(isRejected
                            ? { bgcolor: 'rgba(148, 163, 184, 0.18)' }
                            : outOfRange
                              ? { bgcolor: 'rgba(220, 38, 38, 0.08)' }
                              : {}),
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(row.id)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggleRow(row.id)}
                            inputProps={{ 'aria-label': `Select ${row.id}` }}
                          />
                        </TableCell>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.value.toFixed(2)}</TableCell>
                        <TableCell>{row.reportedResult}</TableCell>
                        <TableCell>{row.releaseStatus}</TableCell>
                        <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Details">
                              <IconButton
                                size="small"
                                sx={{ color: '#334155' }}
                                onClick={() =>
                                  navigate(
                                    `/mock/lab-sample/${batchId}/${encodeURIComponent(row.id)}?value=${encodeURIComponent(row.value.toFixed(2))}&status=${encodeURIComponent(row.releaseStatus)}&result=${encodeURIComponent(row.reportedResult)}`
                                  )
                                }
                              >
                                <SearchOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                sx={{ color: '#475569' }}
                                onClick={() => {
                                  setRejectingRowId(row.id);
                                  setRejectReason('');
                                }}
                              >
                                <DeleteSweepOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Dialog
        open={Boolean(rejectingRowId)}
        onClose={() => {
          setRejectingRowId(null);
          setRejectReason('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject Sample</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Confirm rejection for {rejectingRowId}. Select a reason before confirming.
          </DialogContentText>
          <TextField
            select
            label="Reason"
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            fullWidth
            size="small"
          >
            {rejectReasons.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectingRowId(null);
              setRejectReason('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmRejectRow} disabled={!rejectReason}>
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
